import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { BridgeHTTPResponse, WorkflowRun } from '@smartsheet-bridge/bridge-sdk';
import i18n from '../../../i18n';
import { createAddStepVisitor } from '../../../utils/addStepVisitor';
import { createFilterPathVisitor } from '../../../utils/filterPathVisitor';
import { buildWorkflowTree } from '../../../utils/workflowTree';
import { WorkflowFns } from '../../../utils/workflowVisitor';
import api from '../../api/WorkflowService/WorkflowService';
import { StoreState } from '../../store/store';
import { UIWorkflowRunState, setRunDetailsById } from '../workflowDuck/workflowDuck';

const { t: translate } = i18n;

export interface WorkflowRunState {
  selectedRunId: string | undefined;
  isLoading: boolean;
  hasError: boolean;
  runStepsById: { [key: string]: AnyRunStepPayload };
  visibleRunStepIds: string[];
}

export const initialState: WorkflowRunState = {
  isLoading: false,
  hasError: false,
  selectedRunId: undefined,
  runStepsById: {},
  visibleRunStepIds: [],
};

// TODO: Update BridgeSDK to include runtimeData and remove this
interface ExtendedWorkflowRun extends WorkflowRun {
  runtimeData?: {
    workflowRuntimeData?: any;
    workflowTransits?: any;
    errorInfo?: ExtendedWorkflowRunError[];
  };
}

interface ExtendedWorkflowRunError {
  errorMessage: string;
}

export const fetchWorkflowRun = createAsyncThunk(
  'runSteps/fetchWorkflowRun',
  async (_, { getState, dispatch }) => {
    const selectedRun = selectedRunSelector(getState() as StoreState);
    if (!selectedRun) {
      throw new Error('no selected workflow run');
    }

    // TODO: Update BridgeSDK to include runtimeData and remove this explicit type
    const { data: workflowRun }: BridgeHTTPResponse<ExtendedWorkflowRun> =
      await api.workflowRun.fetch(
        {
          objectUUID: selectedRun.objectUUID,
          requestUUID: selectedRun.requestUUID,
        },
        {
          params: {
            workflowdata: true,
          },
        }
      );

    dispatch(setRunDetailsById(workflowRun));
    const dispatchRunStep = (step: AnyRunStepPayload) =>
      dispatch(addRunStep(step));

    dispatchRunStep({
      id: 'runtime',
      name: 'Trigger',
      type: 'TRIGGER',
      state: UIWorkflowRunState.DONE,
      output: workflowRun.runtimeData?.workflowRuntimeData,
    });

    if (!workflowRun.runtimeData?.workflowTransits) {
      throw new Error('Workflow run does not include runtime data');
    }

    const workflowRunTree = buildWorkflowTree({
      startState: workflowRun.startState,
      transitMap: workflowRun.runtimeData.workflowTransits,
    });

    const filterPathVisitor = createFilterPathVisitor({
      currentTransit: workflowRun.currentTransit,
    });

    let errorMessage = undefined;

    if (
      workflowRun.runtimeData.errorInfo?.length &&
      workflowRun.runtimeData.errorInfo[0]
    ) {
      errorMessage = workflowRun.runtimeData.errorInfo[0].errorMessage;
    }

    const addStepVisitor = createAddStepVisitor({
      dispatch: dispatchRunStep,
      workflowState: workflowRun.state,
      currentTransit: workflowRun.currentTransit,
      errorMsg: errorMessage,
    });
    WorkflowFns.traverseDepthFirst(
      filterPathVisitor,
      addStepVisitor
    )(workflowRunTree);

    return workflowRun;
  }
);

interface BaseRunStepPayload<T extends string> {
  type: T;
  id: string;
  name: string;
  state: UIWorkflowRunState;
}

export interface RunStepPayloadWithError<T extends string>
  extends BaseRunStepPayload<T> {
  state: UIWorkflowRunState.STOPPED_BY_ERROR;
  error: string;
}

export interface RunStepPayloadWithOutput<T extends string, O extends any>
  extends BaseRunStepPayload<T> {
  state: UIWorkflowRunState.DONE;
  output?: O;
}

export interface RunStepPayloadWithErrorAndOutput<
  T extends string,
  O extends any
> extends BaseRunStepPayload<T> {
  state: UIWorkflowRunState.STOPPED_BY_ERROR;
  output?: O;
  error: string;
}

export type RunStepPayload<T extends string, O extends any> =
  | BaseRunStepPayload<T>
  | RunStepPayloadWithError<T>
  | RunStepPayloadWithOutput<T, O>
  | RunStepPayloadWithErrorAndOutput<T, O>;

interface AnyOutput {
  [key: string]: any;
}

export type TriggerRunStepPayload = RunStepPayload<'TRIGGER', AnyOutput>;
export type StateRunStepPayload = RunStepPayload<'STATE', string>;
export type JunctionRunStepPayload = RunStepPayload<'JUNCTION', string>;
export type GroupRunStepPayload = RunStepPayload<'GROUP', AnyOutput>;
export type ModuleRunStepPayload = RunStepPayload<'MODULE', AnyOutput>;
export type ChildRunStepPayload = RunStepPayload<'CHILD', any>;
export type AnyRunStepPayload =
  | TriggerRunStepPayload
  | StateRunStepPayload
  | JunctionRunStepPayload
  | ModuleRunStepPayload
  | GroupRunStepPayload
  | ChildRunStepPayload;

export const isTypeOfTrigger = (
  step: AnyRunStepPayload
): step is TriggerRunStepPayload => step.type === 'TRIGGER';
export const isTypeOfState = (
  step: AnyRunStepPayload
): step is StateRunStepPayload => step.type === 'STATE';
export const isTypeOfModule = (
  step: AnyRunStepPayload
): step is ModuleRunStepPayload => step.type === 'MODULE';
export const isTypeOfJunction = (
  step: AnyRunStepPayload
): step is JunctionRunStepPayload => step.type === 'JUNCTION';
export const isTypeOfGroup = (
  step: AnyRunStepPayload
): step is GroupRunStepPayload => step.type === 'GROUP';
export const isTypeOfChild = (
  step: AnyRunStepPayload
): step is ChildRunStepPayload => step.type === 'CHILD';
export const hasError = <T extends string>(
  step: RunStepPayload<T, any>
): step is RunStepPayloadWithError<T> =>
  step.state === UIWorkflowRunState.STOPPED_BY_ERROR;

export const isStepDone = <T extends string, O>(
  step: RunStepPayload<T, O>
): step is RunStepPayloadWithOutput<T, O> =>
  step.state === UIWorkflowRunState.DONE;

export const hasOutput = <T extends string, O>(
  step: RunStepPayload<T, O>
): step is RunStepPayloadWithOutput<T, O> =>
  step.state === UIWorkflowRunState.DONE;

// TODO: utility function, not used in the file, hence no reason to be here
export const getRunStateString = (state: UIWorkflowRunState): string => {
  switch (state) {
    case UIWorkflowRunState.DONE:
      return translate('designer:runLog.completed');
    case UIWorkflowRunState.STOPPED_BY_ERROR:
      return translate('designer:runLog.stoppedByError');
    case UIWorkflowRunState.STOPPED:
      return translate('designer:runLog.stopped');
    case UIWorkflowRunState.WAITING_ON_CHATBOT_REPLY:
      return translate('designer:runLog.runningInProgress');
    case UIWorkflowRunState.WAITING:
      return translate('designer:runLog.runningInProgress');
    case UIWorkflowRunState.IN_PROGRESS:
    default:
      return translate('designer:runLog.runningInProgress');
  }
};

export const getRunStateCategory = (state: UIWorkflowRunState) => {
  if (
    state >= UIWorkflowRunState.IN_PROGRESS &&
    state < UIWorkflowRunState.WAITING
  ) {
    return UIWorkflowRunState.IN_PROGRESS;
  } else if (
    state >= UIWorkflowRunState.WAITING &&
    state < UIWorkflowRunState.STOPPED
  ) {
    return UIWorkflowRunState.WAITING;
  } else if (
    state >= UIWorkflowRunState.STOPPED &&
    state < UIWorkflowRunState.DONE
  ) {
    return UIWorkflowRunState.STOPPED;
  } else {
    return UIWorkflowRunState.DONE;
  }
};

export const WorkflowRunSlice = createSlice({
  name: 'workflowRun',
  initialState,
  reducers: {
    setSelectedRunId: (state, action: PayloadAction<string | undefined>) => {
      state.selectedRunId = action.payload;
    },
    addRunStep: (state, action: PayloadAction<AnyRunStepPayload>) => {
      state.runStepsById[action.payload.id] = action.payload;
      state.visibleRunStepIds.push(action.payload.id);
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchWorkflowRun.pending, state => {
      state.hasError = false;
      state.isLoading = true;
      state.runStepsById = {};
      state.visibleRunStepIds = [];
    });
    builder.addCase(fetchWorkflowRun.rejected, state => {
      state.hasError = true;
      state.isLoading = false;
    });
    builder.addCase(fetchWorkflowRun.fulfilled, state => {
      state.hasError = false;
      state.isLoading = false;
    });
  },
});

export const selectedRunSelector = createSelector(
  (state: StoreState) => state.workflowRun.selectedRunId,
  (state: StoreState) => state.workflow.runsById,
  (selectedRunId, runsById) =>
    typeof selectedRunId !== 'string' ? undefined : runsById[selectedRunId]
);

export const allStepsSector = createSelector(
  (state: StoreState) => state.workflowRun.visibleRunStepIds,
  (state: StoreState) => state.workflowRun.runStepsById,
  (visibleRunStepIds, runStepsById) =>
    visibleRunStepIds.map(stepId => runStepsById[stepId])
);

export const stepSelector = (stepId: string) => (state: StoreState) =>
  state.workflowRun.runStepsById[stepId];

export const selectedRunIdSelector = (state: StoreState) =>
  state.workflowRun.selectedRunId;

export const workflowRunStepsIsLoadingSelector = (state: StoreState) =>
  state.workflowRun.isLoading;

export const workflowRunStepsHasErrorSelector = (state: StoreState) =>
  state.workflowRun.hasError;

export const { setSelectedRunId, addRunStep } = WorkflowRunSlice.actions;
export default WorkflowRunSlice.reducer;
